import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Center from '../../components/css/Center';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import Toast from '../../components/forms/Toast';
import { apiRequest, formatRelativeTime } from '../../utils';

const LIMIT = 20;

const PLATFORMS = [
  { name: 'instagram', label: 'Instagram', match: /instagram\.|instagr\.am/i },
  { name: 'tiktok',    label: 'TikTok',    match: /tiktok\./i, icon: 'tik-tok' },
  { name: 'youtube',   label: 'YouTube',   match: /youtube\.|youtu\.be/i },
  { name: 'linkedin',  label: 'LinkedIn',  match: /linkedin\./i },
  { name: 'facebook',  label: 'Facebook',  match: /facebook\.|fb\.watch|fb\.com/i },
];

const detectPlatform = (url) => {
  if (!url) return null;
  return PLATFORMS.find((p) => p.match.test(url)) || null;
};

// Returns { platform, src, kind } where kind is 'iframe-fixed' (uses CSS height per platform)
// or 'iframe-ratio' for 16:9 video. Returns null if we don't know how to embed.
const buildEmbed = (url) => {
  if (!url) return null;
  const cleaned = url.split('?')[0].split('#')[0];

  // Instagram: posts, reels, IGTV
  let m = cleaned.match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/i);
  if (m) return { platform: 'instagram', src: `https://www.instagram.com/p/${m[1]}/embed`, kind: 'iframe-fixed' };

  // TikTok
  m = cleaned.match(/tiktok\.com\/(?:@[^/]+\/video|v)\/(\d+)/i);
  if (m) return { platform: 'tiktok', src: `https://www.tiktok.com/embed/v2/${m[1]}`, kind: 'iframe-fixed' };

  // YouTube — watch, short link, shorts
  m =
    cleaned.match(/youtube\.com\/watch\?.*v=([A-Za-z0-9_-]{11})/i) ||
    url.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
    cleaned.match(/youtu\.be\/([A-Za-z0-9_-]{11})/i) ||
    cleaned.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/i) ||
    cleaned.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/i);
  if (m) return { platform: 'youtube', src: `https://www.youtube.com/embed/${m[1]}`, kind: 'iframe-ratio' };

  // LinkedIn — only the activity-id form is easily embeddable
  m = url.match(/(?:urn:li:activity:|activity-|activity:)(\d{15,})/i);
  if (m) return { platform: 'linkedin', src: `https://www.linkedin.com/embed/feed/update/urn:li:activity:${m[1]}`, kind: 'iframe-fixed' };

  // Facebook
  if (/facebook\.|fb\.com|fb\.watch/i.test(url)) {
    return {
      platform: 'facebook',
      src: `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=true&width=500`,
      kind: 'iframe-fixed',
    };
  }

  return null;
};

const SocialEmbed = ({ url }) => {
  const embed = buildEmbed(url);
  const platform = detectPlatform(url);

  if (embed) {
    return (
      <div className={`social-embed social-embed--${embed.platform} social-embed--${embed.kind}`}>
        <iframe
          src={embed.src}
          title={`${embed.platform} post`}
          frameBorder="0"
          scrolling="no"
          allowFullScreen
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        />
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="social-embed-fallback"
      onClick={(e) => e.stopPropagation()}
    >
      <span className={`social-embed-fallback__badge social-embed-fallback__badge--${platform?.name || 'link'}`}>
        <Icon name={platform?.icon || platform?.name || 'globe'} pack={platform ? 'brands' : undefined} theme="light" />
      </span>
      <span className="social-embed-fallback__text">
        <span className="social-embed-fallback__title">
          {platform ? `View on ${platform.label}` : 'View post'}
        </span>
        <span className="social-embed-fallback__url">{url}</span>
      </span>
      <Icon name="arrow-up-right-from-square" className="social-embed-fallback__chev" />
    </a>
  );
};

const FeedPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const sentinelRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const fetchFeed = async ({ cursor = null, append = false } = {}) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      setError(null);

      const qs = new URLSearchParams();
      qs.set('limit', String(LIMIT));
      if (cursor) qs.set('cursor', cursor);

      const data = await apiRequest('GET', `/feed?${qs.toString()}`);

      const newItems = (data.items || []).filter((it) => {
        const id = it?.payload?.submission?.id || it?.cursor;
        if (!id) return true;
        if (seenIdsRef.current.has(id)) return false;
        seenIdsRef.current.add(id);
        return true;
      });

      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setNextCursor(data.next_cursor || null);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      setError(err.message || 'genericError');
      setShowToast(true);
    } finally {
      if (!append) setLoading(false);
      else setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchFeed({ cursor: null, append: false });
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (!hasMore) return;
        if (loading || loadingMore) return;
        if (!nextCursor) return;

        fetchFeed({ cursor: nextCursor, append: true });
      },
      { root: null, rootMargin: '800px 0px', threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, nextCursor, loading, loadingMore]);

  const goToTeam = (teamId) => {
    if (teamId) sessionStorage.setItem('otherTeamId', teamId);
    navigate('/team');
  };

  const renderHeader = (sub) => {
    const teamPhoto = sub?.team?.photo_url;
    const teamName = sub?.team?.name;
    const companyName = sub?.company?.name;
    return (
      <header
        className="feed-post__header feed-post__header--clickable"
        onClick={(e) => { e.stopPropagation(); goToTeam(sub?.team_id); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') goToTeam(sub?.team_id); }}
      >
        {teamPhoto && (
          <Image url={teamPhoto} size="2.4rem" className="circle feed-post__avatar" />
        )}
        <div className="feed-post__author">
          <div className="feed-post__team">{teamName}</div>
          {companyName && (
            <div className="feed-post__company">
              <Icon name="building" /> {companyName}
            </div>
          )}
        </div>
      </header>
    );
  };

  const renderBody = (sub, mission, captionAuthor) => (
    <div className="feed-post__body">
      {sub?.caption ? (
        <p className="feed-post__caption">
          <strong>{captionAuthor}</strong> {sub.caption}
        </p>
      ) : mission?.description ? (
        <p className="feed-post__caption feed-post__caption--muted">
          {mission.description}
        </p>
      ) : null}

      {sub?.uploaded_at && (
        <div className="feed-post__time">
          {formatRelativeTime(sub.uploaded_at, i18n.language)}
        </div>
      )}
    </div>
  );

  const renderItem = (item) => {
    if (item.type === 'mission_submission') {
      const sub = item.payload?.submission;
      const mission = item.payload?.mission;
      const teamName = sub?.team?.name;
      const missionImage =
        sub?.image_url || mission?.photo_url || require('./../../assets/images/fallback/mission.png');

      return (
        <Card
          key={sub?.id || item.cursor}
          mx="auto"
          mb="3"
          p="0"
          className="feed-post"
          style={{ maxWidth: '26rem' }}
        >
          {renderHeader(sub)}

          <div
            className="feed-post__media feed-post__media--clickable"
            onClick={() => goToTeam(sub?.team_id)}
          >
            <Image url={missionImage} size="100%" className="feed-post__image" />
            {mission?.name && (
              <span className="feed-post__mission-tag">
                <Icon name="camera" theme="light" className="me-1" />
                {mission.name}
              </span>
            )}
          </div>

          {renderBody(sub, mission, teamName)}
        </Card>
      );
    }

    if (item.type === 'social_submission') {
      const sub = item.payload?.submission;
      const mission = item.payload?.mission;
      const teamName = sub?.team?.name;
      const url = sub?.social_media_url;
      const platform = detectPlatform(url);

      // Suppress the caption when it's just the post URL — the embed already
      // shows the link, so repeating it underneath looks like junk.
      const rawCaption = (sub?.caption || '').trim();
      const captionIsUrl = /^https?:\/\/\S+$/i.test(rawCaption) || rawCaption === url;
      const subForBody = captionIsUrl ? { ...sub, caption: null } : sub;

      return (
        <Card
          key={sub?.id || item.cursor}
          mx="auto"
          mb="3"
          p="0"
          className="feed-post feed-post--social"
          style={{ maxWidth: '26rem' }}
        >
          {renderHeader(sub)}

          <SocialEmbed url={url} />

          {mission?.name && (
            <div className="feed-post__mission-row">
              <span className={`feed-post__mission-chip feed-post__mission-chip--${platform?.name || 'social'}`}>
                {platform && (
                  <Icon name={platform.icon || platform.name} pack="brands" className="me-1" />
                )}
                {mission.name}
              </span>
            </div>
          )}

          {renderBody(subForBody, mission, teamName)}
        </Card>
      );
    }

    return (
      <Card key={item.cursor} mx="3" mb="2">
        <p style={{ margin: 0 }}>{t('unsupportedFeedItem')}</p>
      </Card>
    );
  };

  return (
    <Base activeNav="feed">
      <Title>
        <Icon name="comment-alt" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('feed')}
      </Title>

      {loading && items.length === 0 && (
        <Loading size="lg" />
      )}

      {!loading && items.length === 0 && (
        <div className="feed-empty">
          <div className="feed-empty__icon">
            <Icon name="camera" theme="primary" />
          </div>
          <div className="feed-empty__text">{t('noFeedItems')}</div>
        </div>
      )}

      {items.map(renderItem)}

      <div ref={sentinelRef} style={{ height: '1px' }} />

      {loadingMore && (
        <Center>
          <Loading inline label={t('loadingMore')} className="mt-3" />
        </Center>
      )}

      {!hasMore && items.length > 0 && (
        <div className="feed-end">
          <span className="feed-end__dash" />
          <span className="feed-end__text">{t('endOfFeed')}</span>
          <span className="feed-end__dash" />
        </div>
      )}

      <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />
    </Base>
  );
};

export default FeedPage;
